import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Global axios defaults - send/receive cookies on every call
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

/**
 * Normalize FastAPI error payload → string. FastAPI 422 returns arrays of
 * `{msg, ...}` objects which crash React if rendered directly.
 */
export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

async function tryRefreshSession() {
  await axios.post(`${API}/auth/refresh`);
}

/** Where staff land after login when no explicit redirect was requested. */
export function defaultConsolePath(role) {
  if (role === "sales") return "/console/sales";
  if (role === "admin") return "/console/admin";
  return "/console";
}

export function AuthProvider({ children }) {
  // user states: undefined = still checking, null = anonymous, obj = logged in
  const [user, setUser] = useState(undefined);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch {
      try {
        // Access cookie expired - rotate from refresh cookie
        await tryRefreshSession();
        const { data } = await axios.get(`${API}/auth/me`);
        setUser(data);
      } catch {
        setUser(null);
      }
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Soft-refresh access tokens when any API call returns 401 once
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      async (error) => {
        const original = error.config;
        if (
          error.response?.status === 401 &&
          original &&
          !original._retried &&
          !String(original.url || "").includes("/auth/")
        ) {
          original._retried = true;
          try {
            await tryRefreshSession();
            return axios(original);
          } catch {
            setUser(null);
          }
        }
        return Promise.reject(error);
      },
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    setUser(data);
    return data;
  };

  const register = async (name, email, password) => {
    const { data } = await axios.post(`${API}/auth/register`, {
      name,
      email,
      password,
    });
    // Pending email verification - do not treat as logged-in session
    if (data?.requires_confirmation) {
      return data;
    }
    setUser(data);
    return data;
  };

  const confirm = async (email, code, password) => {
    const { data } = await axios.post(`${API}/auth/confirm`, {
      email,
      code,
      password,
    });
    setUser(data);
    return data;
  };

  const resendConfirmation = async (email) => {
    const { data } = await axios.post(`${API}/auth/resend-confirmation`, { email });
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {}
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{ user, login, register, confirm, resendConfirmation, logout, refresh }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
